import { Container } from "../../../reducers/componentSearch";
import { Formik } from "formik";
import { FormContainer, InputLabel } from "../../../constants/Styles";
import FormikNumberInput from "../../formik-form-components/FormikNumberInput";
import ContainerFormButtons from "../../component-search/PartDrawer/Inventory/PhysicalInventory/ContainerFormButtons";
import Button from "../../common/Button";
import React from "react";
import * as Yup from "yup";
import useUpdateAllocation from "../../../utils/hooks/inventory/updateAllocation";
import { ModalContainer, Title, VerticalList } from "./BoardInventoryStyles";
import { useCustomAlert } from "../../../utils/hooks/alerts";
import AllocationsSummary from "../parts/InventoryUpdates/AllocationsSummary";

interface AdjustAllocationFormValues {
  qtyToAllocate: number;
}

export default function AdjustAllocation({
  container,
  totalQuantityAllocated,
  quantityAllocatedFromThisContainer,
  requiredAmount,
  boardId,
  shipmentId,
  closeModal,
}: {
  container: Container;
  totalQuantityAllocated: number;
  quantityAllocatedFromThisContainer: number;
  requiredAmount: number;
  boardId: string;
  shipmentId: string;
  closeModal: () => void;
}) {
  const customAlert = useCustomAlert();
  const onSuccess = () => {
    closeModal();
  };

  const onError = (detail: string) => {
    customAlert(detail);
  };
  const { updateAllocation, loading } = useUpdateAllocation(onSuccess, onError);
  const adjustAllocationSchema = Yup.object().shape({
    qtyToAllocate: Yup.number()
      .min(0)
      .max(
        Math.min(
          requiredAmount,
          container.last_counted_amount_less_spent_allocations +
            quantityAllocatedFromThisContainer,
        ),
        `Quantity cannot exceed the required amount for the project or the number of parts that exist for that container`,
      )
      .required("Quantity is required")
      .nullable(),
  });

  const onSubmit = (values: AdjustAllocationFormValues) => {
    updateAllocation({
      flashpcb_container_barcode: container.flashpcb_container_barcode,
      board_id: boardId,
      shipment_id: shipmentId,
      old_allocated_amount: quantityAllocatedFromThisContainer,
      new_allocated_amount: values.qtyToAllocate,
    });
  };

  return (
    <ModalContainer>
      <Title>{`Adjust allocation for ${container.flashpcb_container_barcode}`}</Title>
      <VerticalList>
        <>
          <InputLabel>Required amount for project: </InputLabel>
          {requiredAmount}
        </>
        <>
          <InputLabel>Amount still needed: </InputLabel>
          {requiredAmount - totalQuantityAllocated}
        </>
        <>
          <InputLabel>Available to allocate from container: </InputLabel>
          {container.available_inventory_level +
            quantityAllocatedFromThisContainer}
        </>
      </VerticalList>

      <Formik
        initialValues={{
          qtyToAllocate: quantityAllocatedFromThisContainer,
        }}
        onSubmit={onSubmit}
        validationSchema={adjustAllocationSchema}
      >
        {({ handleSubmit, isValid, dirty }) => {
          return (
            <FormContainer onSubmit={handleSubmit}>
              <FormikNumberInput
                name="qtyToAllocate"
                label={"Quantity to allocate"}
                disabled={false}
                autoFocus={true}
              />
              <ContainerFormButtons
                onCancel={closeModal}
                loading={loading}
                isDisabled={!(isValid && dirty)}
                onSubmit={handleSubmit}
              />
              {quantityAllocatedFromThisContainer > 0 && (
                <Button
                  label={"Remove existing allocation"}
                  onClick={() =>
                    updateAllocation({
                      flashpcb_container_barcode:
                        container.flashpcb_container_barcode,
                      board_id: boardId,
                      shipment_id: shipmentId,
                      new_allocated_amount: 0,
                      old_allocated_amount: quantityAllocatedFromThisContainer,
                    })
                  }
                />
              )}
            </FormContainer>
          );
        }}
      </Formik>
      <AllocationsSummary container={container} shipmentId={shipmentId} />
    </ModalContainer>
  );
}
